import { Badge, Callout, EmptyState, Table, Td } from '../ui/primitives';
import { pct } from '../../lib/format';
import type { ProviderRow } from '../../lib/types';

/**
 * Per-receiving-provider funnel.
 *
 * "Accepted" is the receiving server returning 2xx. Inbox placement is a
 * separate column and is blank unless seed testing measured it, because no
 * amount of SMTP data can tell you which folder a message landed in.
 */
export function ProviderTable({ rows }: { rows: ProviderRow[] }) {
  if (!rows.length) {
    return <EmptyState title="No sending activity in this period" />;
  }

  return (
    <div className="space-y-4">
      <Callout tone="info">
        <strong>Accepted</strong> means the receiving server returned a success code. It is not the same
        as inbox placement. The <strong>Inbox</strong> column is populated only where a seed test has
        actually measured the folder, and stays blank otherwise rather than guessing.
      </Callout>

      <Table
        head={[
          'Receiving provider',
          'Sent',
          'Accepted',
          'Deferred',
          'Bounced',
          'Blocked',
          'Complaints',
          'Open rate (human / raw)',
          'Inbox (measured)',
        ]}
      >
        {rows.map((row) => (
          <tr key={row.providerFamily} className={row.providerFamily === 'microsoft' ? 'bg-sky-50/40' : ''}>
            <Td>
              <div className="font-medium text-ink-900">{row.label}</div>
              {row.providerFamily === 'microsoft' && (
                <span className="text-xs text-sky-700">Includes Microsoft 365 business domains</span>
              )}
            </Td>
            <Td className="tabular-nums">{row.sent.toLocaleString()}</Td>
            <Td className="tabular-nums">
              {row.accepted.toLocaleString()}
              <span className="ml-1 text-xs text-ink-400">{pct(row.acceptanceRate)}</span>
            </Td>
            <Td className="tabular-nums">{row.deferred.toLocaleString()}</Td>
            <Td className="tabular-nums">
              {row.bounced.toLocaleString()}
              {row.hardBounced > 0 && (
                <span className="ml-1 text-xs text-red-600">{row.hardBounced} hard</span>
              )}
            </Td>
            <Td className="tabular-nums">
              {row.blocked > 0 ? <Badge tone="bad">{row.blocked}</Badge> : '0'}
            </Td>
            <Td className="tabular-nums">
              {row.complaints.toLocaleString()}
              <span className="ml-1 text-xs text-ink-400">{pct(row.complaintRate, 3)}</span>
            </Td>
            <Td className="tabular-nums">
              <span className="font-medium text-ink-900">{pct(row.openRate.filtered)}</span>
              <span className="ml-1 text-xs text-ink-400">/ {pct(row.openRate.raw)}</span>
            </Td>
            <Td>
              {row.inboxRate === null ? (
                <span className="text-xs text-ink-400">not measured</span>
              ) : (
                <div>
                  <Badge tone={row.inboxRate >= 85 ? 'good' : row.inboxRate >= 70 ? 'warn' : 'bad'}>
                    {pct(row.inboxRate, 0)}
                  </Badge>
                  <span className="ml-1 text-xs text-ink-400">n={row.seedSampleSize}</span>
                </div>
              )}
            </Td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
